import { cn } from '@/lib/utils'
import { trackAppStoreClick } from '@/lib/analytics'
import {
  getStoreUrl,
  getChromeStoreUrl,
  detectPlatform,
  type StorePlatform,
  type DevicePlatform,
} from '@/lib/storeLinks'

/** Apple logo path — shared with TranslatorAlsoOnLinks. */
function AppleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" role="img" aria-label="Apple">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  )
}

/** Google Play logo path — shared with TranslatorAlsoOnLinks. */
function PlayGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" role="img" aria-label="Google Play">
      <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
    </svg>
  )
}

/** Google Chrome logo path — shared with TranslatorAlsoOnLinks. */
function ChromeGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" role="img" aria-label="Google Chrome">
      <path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-5.344 9.257c.206.01.413.016.621.016 6.627 0 12-5.373 12-12 0-1.54-.29-3.011-.818-4.364zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728Z" />
    </svg>
  )
}

/** Shared dark, 6px-radius store-badge chrome — two-line label, always. */
function darkBadgeClass(variant: 'pill' | 'solid'): string {
  return cn(
    'inline-flex items-center gap-2.5 rounded-md transition-all duration-150 hover:brightness-110 active:scale-[0.98] shadow-xs cursor-pointer select-none',
    variant === 'solid' ? 'h-12 px-4' : 'h-10 px-3.5',
  )
}

/** Chrome Web Store badge — the desktop-relevant install surface. */
function ChromeButton({ placement, variant }: { placement: string; variant: 'pill' | 'solid' }) {
  return (
    <a
      href={getChromeStoreUrl(placement)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackAppStoreClick('chrome', placement)}
      className={darkBadgeClass(variant)}
      style={{ background: 'var(--ink)', color: 'var(--cream)' }}
    >
      <ChromeGlyph className="h-[18px] w-[18px] shrink-0" />
      <span className="flex flex-col items-start leading-tight" dir="ltr">
        <span className="text-[10px] font-medium opacity-70">Add to</span>
        <span className="text-[13px] font-semibold">Chrome</span>
      </span>
    </a>
  )
}

function StoreButton({
  platform,
  placement,
  variant,
}: {
  platform: StorePlatform
  placement: string
  variant: 'pill' | 'solid'
}) {
  const isIos = platform === 'ios'
  const label = isIos ? 'App Store' : 'Google Play'
  const sub = isIos ? 'Download on the' : 'Get it on'
  return (
    <a
      href={getStoreUrl(platform, placement)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackAppStoreClick(platform, placement)}
      className={darkBadgeClass(variant)}
      style={{ background: 'var(--ink)', color: 'var(--cream)' }}
    >
      {isIos ? (
        <AppleGlyph className="h-[18px] w-[18px] shrink-0" />
      ) : (
        <PlayGlyph className="h-[18px] w-[18px] shrink-0" />
      )}
      <span className="flex flex-col items-start leading-tight" dir="ltr">
        <span className="text-[10px] font-medium opacity-70">{sub}</span>
        <span className="text-[13px] font-semibold">{label}</span>
      </span>
    </a>
  )
}

/**
 * Phone-store-only badges (no Chrome badge, no device detection) plus the
 * "Pro carries over" line. Used anywhere the surrounding copy is already the
 * install pitch, e.g. Settings › Support and the paywall.
 */
export function AppStoreLinks({
  placement,
  variant = 'pill',
  showNote = true,
  className,
}: {
  placement: string
  variant?: 'pill' | 'solid'
  /** Drop the "Pro carries over" line where the surrounding copy already says it. */
  showNote?: boolean
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-2.5', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <StoreButton platform="ios" placement={placement} variant={variant} />
        <StoreButton platform="android" placement={placement} variant={variant} />
      </div>
      {showNote && (
        <p className="text-[12px]" style={{ color: 'var(--muted-2)' }}>
          Pro carries over. Sign in with the same email.
        </p>
      )}
    </div>
  )
}

/**
 * Device-aware store badges. On iOS shows just the App Store, on Android just
 * Google Play, on desktop both. Pass `platform` to force a specific set.
 * `placement` flows into UTM attribution + click tracking.
 */
export function StoreBadges({
  placement,
  variant = 'pill',
  platform,
  className,
}: {
  placement: string
  variant?: 'pill' | 'solid'
  platform?: DevicePlatform
  className?: string
}) {
  const device = platform ?? detectPlatform()
  const show: StorePlatform[] =
    device === 'ios' ? ['ios'] : device === 'android' ? ['android'] : ['ios', 'android']
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {/* Desktop: the extension is the surface people can actually install
          right now, so it leads; the phone badges follow. */}
      {device === 'desktop' && <ChromeButton placement={placement} variant={variant} />}
      {show.map((p) => (
        <StoreButton key={p} platform={p} placement={placement} variant={variant} />
      ))}
    </div>
  )
}
